/** Schema-driven editor for a tag's `data`.
 *
 * Renders each schema property (grouped by `x-group`, ordered by `x-displayOrder`)
 * with a type/flag-appropriate widget (see TagDataField). Groups are collapsible;
 * collapsed groups summarise their non-empty field names. Data keys NOT described
 * by the schema are collected into a separate "Additional fields" section — the
 * console equivalent of the dashboard's (never-shipped) extra-attributes handling.
 * When the category has no schema, every key falls into that section.
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { groupedProps, isRequired, type SchemaProp, type TagSchema } from '../schema/vocabulary';
import { TagDataField } from './TagDataField';
import type { BindingsByAttribute } from './translationBindings';

interface Props {
  schema: TagSchema | null | undefined;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly?: boolean;
  /** Translation bindings of the tag's leaves (metaplan A6): auto-translated leaves are marked. */
  translationBindings?: BindingsByAttribute;
}

function hasValue(v: unknown): boolean {
  if (v == null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return true;
}

export function TagDataEditor({ schema, value, onChange, readOnly = false, translationBindings }: Props) {
  const sections = useMemo(() => groupedProps(schema), [schema]);
  const schemaKeys = useMemo(() => new Set(Object.keys(schema?.properties ?? {})), [schema]);
  const extraKeys = useMemo(
    () => Object.keys(value ?? {}).filter((k) => !schemaKeys.has(k)),
    [value, schemaKeys],
  );
  // Track collapsed groups (default: all expanded).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const setKey = (key: string, next: unknown) => {
    const out = { ...value };
    if (next === undefined) delete out[key];
    else out[key] = next;
    onChange(out);
  };

  const hasSchemaFields = sections.length > 0;

  const nonEmptyNames = (entries: [string, SchemaProp][]) =>
    entries.filter(([k]) => hasValue(value?.[k])).map(([k, prop]) => prop.title || k);

  return (
    <Box>
      {!hasSchemaFields && extraKeys.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This category has no schema. Add fields below.
        </Typography>
      )}

      <Stack spacing={1}>
        {sections.map((section) => {
          const id = section.group?.name ?? '__ungrouped__';
          const title = section.group ? section.group.title || section.group.name : 'General';
          const isOpen = !collapsed.has(id);
          const summary = nonEmptyNames(section.entries);
          return (
            <Accordion
              key={id}
              disableGutters
              slotProps={{ transition: { unmountOnExit: true } }}
              expanded={isOpen}
              onChange={(_, open) =>
                setCollapsed((prev) => {
                  const s = new Set(prev);
                  if (open) s.delete(id);
                  else s.add(id);
                  return s;
                })
              }
              sx={{ '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2">{title}</Typography>
                  {!isOpen && summary.length > 0 && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                      {summary.join(', ')}
                    </Typography>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {section.group?.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {section.group.description}
                  </Typography>
                )}
                <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
                  {section.entries.map(([key, prop]) => (
                    <TagDataField
                      key={key}
                      propKey={key}
                      prop={prop}
                      value={value?.[key]}
                      required={isRequired(schema, key)}
                      readOnly={readOnly}
                      onChange={(next) => setKey(key, next)}
                      bindings={translationBindings?.[key]}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      <ExtraAttributes keys={extraKeys} value={value} readOnly={readOnly} knownKeys={schemaKeys} onSet={setKey} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Additional (out-of-schema) fields
// ---------------------------------------------------------------------------

function ExtraAttributes({
  keys,
  value,
  readOnly,
  knownKeys,
  onSet,
}: {
  keys: string[];
  value: Record<string, unknown>;
  readOnly: boolean;
  knownKeys: Set<string>;
  onSet: (key: string, next: unknown) => void;
}) {
  const [newKey, setNewKey] = useState('');
  const addKey = () => {
    const k = newKey.trim();
    if (!k || k in value || knownKeys.has(k)) return;
    onSet(k, '');
    setNewKey('');
  };

  if (keys.length === 0 && readOnly) return null;

  return (
    <Accordion disableGutters defaultExpanded={keys.length > 0}
      sx={{ mt: 1, '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Additional fields (not in schema){keys.length ? ` · ${keys.length}` : ''}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
          {keys.length === 0 && (
            <Typography variant="caption" color="text.secondary">No extra fields.</Typography>
          )}
          {keys.map((key) => (
            <ExtraRow key={key} name={key} value={value[key]} readOnly={readOnly}
              onChange={(next) => onSet(key, next)} onRemove={() => onSet(key, undefined)} />
          ))}
          {!readOnly && (
            <>
              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" label="New field key" value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKey(); } }}
                  sx={{ width: 240 }} />
                <Button size="small" startIcon={<AddIcon />} onClick={addKey} disabled={!newKey.trim()}>
                  Add field
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function ExtraRow({
  name,
  value,
  readOnly,
  onChange,
  onRemove,
}: {
  name: string;
  value: unknown;
  readOnly: boolean;
  onChange: (next: unknown) => void;
  onRemove: () => void;
}) {
  const isObject = value !== null && typeof value === 'object';
  const isBool = typeof value === 'boolean';
  const isNumber = typeof value === 'number';

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ width: 160, pt: 1, flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{name}</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        {isBool ? (
          <FormControlLabel
            control={<Switch size="small" checked={value === true} disabled={readOnly}
              onChange={(e) => onChange(e.target.checked)} />}
            label={String(value)}
          />
        ) : isNumber ? (
          <TextField type="number" size="small" fullWidth disabled={readOnly} value={String(value)}
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
        ) : isObject ? (
          <TextField
            size="small" fullWidth multiline minRows={2} maxRows={10} disabled={readOnly}
            defaultValue={JSON.stringify(value, null, 2)}
            InputProps={{ sx: { fontFamily: 'monospace' } }}
            helperText="JSON — parsed on change; invalid JSON is stored as text."
            onChange={(e) => {
              try { onChange(JSON.parse(e.target.value)); } catch { onChange(e.target.value); }
            }}
          />
        ) : (
          <TextField size="small" fullWidth disabled={readOnly} value={value == null ? '' : String(value)}
            onChange={(e) => onChange(e.target.value)} />
        )}
      </Box>
      {!readOnly && (
        <IconButton size="small" color="error" onClick={onRemove} aria-label={`Remove ${name}`}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}
